<template>
  <div class="edit-entry">
    <div v-if="loading" class="text-center py-4"><div class="spinner-border text-secondary" role="status"></div></div>
    <template v-else>
      <p v-if="heading" class="text-secondary text-sm mb-3">{{ heading }}</p>

      <!-- Absence: type + hours -->
      <template v-if="entry.isAbsence">
        <span class="section-label d-block mb-1">{{ $t('timeRegistration.absenceType') }}</span>
        <select v-model="form.typeId" class="form-select mb-3">
          <option v-for="t in absenceTypes" :key="t.id" :value="t.id">{{ absenceLabel(t.type) }}</option>
        </select>
        <span class="section-label d-block mb-1">{{ $t('timeRegistration.absenceHoursLabel') }}</span>
        <div class="stepper">
          <button class="btn btn-outline-secondary mb-0" :disabled="form.absenceHours <= minAbsenceHours" v-bind="stepButton('absenceHours', -1)">−</button>
          <div class="stepper-value"><span :class="{ 'text-danger': showErrors && errors.absenceHours }">{{ fmt(form.absenceHours) }}</span><small>{{ $t('endOfDay.hoursUnit') }}</small></div>
          <button class="btn btn-outline-secondary mb-0" :disabled="form.absenceHours >= maxAbsence" v-bind="stepButton('absenceHours', 1)">+</button>
        </div>
        <p v-if="capAbsence" class="text-xs mt-2 mb-3" :class="form.absenceHours >= maxAbsence ? 'text-dark fw-semibold' : 'text-secondary'">
          {{ absenceDay.work > 0
            ? $t('endOfDay.absenceMaxAfterWork', { hours: fmt(maxAbsence), scheduled: fmt(absenceDay.scheduled), work: fmt(absenceDay.work) })
            : $t('endOfDay.absenceMax', { hours: fmt(maxAbsence) }) }}
        </p>
        <div v-else class="mb-3"></div>
      </template>

      <!-- End-of-day work line: dagvinna/yfirvinna + description (project and phase are fixed —
           to move the hours, delete the line and register a new one) -->
      <template v-else>
        <span class="section-label d-block mb-1">{{ $t('endOfDay.project') }}</span>
        <div class="project-current mb-3">
          <strong v-if="entry.projectKey">{{ entry.projectKey }}</strong> {{ entry.projectName }}<span v-if="entry.projectPhaseName" class="text-secondary"> — {{ entry.projectPhaseName }}</span>
        </div>

        <span class="section-label d-block mb-1">{{ $t('endOfDay.normalHours') }}</span>
        <div class="stepper mb-1">
          <button class="btn btn-outline-secondary mb-0" :disabled="form.normalHours <= 0" v-bind="stepButton('normalHours', -1)">−</button>
          <div class="stepper-value"><input v-model.number="form.normalHours" type="number" min="0" step="0.25" class="form-control text-center" :class="{ 'is-invalid': showErrors && errors.hours }" /></div>
          <button class="btn btn-outline-secondary mb-0" :disabled="form.normalHours >= maxNormal" v-bind="stepButton('normalHours', 1)">+</button>
        </div>
        <p class="text-xs text-secondary mb-3">{{ $t('timeRegistration.maxNormalHoursForDay', { hours: fmt(maxNormal) }) }}</p>

        <span class="section-label overtime-label d-block mb-1">{{ $t('endOfDay.overtimeHours') }}</span>
        <div class="stepper mb-3">
          <button class="btn btn-outline-secondary mb-0" :disabled="form.overtimeHours <= 0" v-bind="stepButton('overtimeHours', -1)">−</button>
          <div class="stepper-value"><input v-model.number="form.overtimeHours" type="number" min="0" step="0.25" class="form-control text-center" :class="{ 'is-invalid': showErrors && errors.hours }" /></div>
          <button class="btn btn-outline-secondary mb-0" :disabled="form.overtimeHours >= maxHours('overtimeHours')" v-bind="stepButton('overtimeHours', 1)">+</button>
        </div>

        <span class="section-label d-block mb-1">{{ $t('endOfDay.description') }} *</span>
        <comment-suggest-input v-model="form.comment" class="mb-3" :user-id="userId" :placeholder="$t('endOfDay.descriptionPlaceholder')" :invalid="showErrors && errors.comment" />
        <template v-if="referenceEnabled">
          <span class="section-label d-block mb-1">{{ $t('endOfDay.reference') }}</span>
          <comment-suggest-input v-model="form.reference" field="reference" class="reference-input mb-3" :user-id="userId" />
        </template>
      </template>

      <p v-if="blockReason" class="text-xs mb-2" :class="showErrors ? 'field-error' : 'text-secondary'">{{ blockReason }}</p>
      <div class="d-flex gap-2 justify-content-end">
        <button class="btn btn-outline-secondary mb-0" @click="$emit('cancel')">{{ $t('common.cancel') }}</button>
        <!-- Stays clickable while incomplete so a click can point at what is missing -->
        <button class="btn btn-dark mb-0" :class="{ 'save-blocked': !!blockReason }" :disabled="saving" @click="onSaveClick">
          <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>{{ $t('common.save') }}
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import api from "../../services/api";
import CommentSuggestInput, { clearCommentSuggestions } from "./CommentSuggestInput.vue";
import { createHoldStepper, nextHours } from "./hoursStepper";

/**
 * EditEntryDialog — edits exactly one end-of-day work line (dagvinna/yfirvinna, description — the
 * project and phase are fixed) or one absence (type, hours) through PUT api/Timesheet/{id}. The server applies the
 * day editor's rules to the line; the dagvinna room shown here (day ceiling minus the user's other
 * lines that day) only guides the input. With capAbsence (end-of-day registration) an absence is
 * likewise capped at the day's scheduled dagvinna minus the dagvinna registered as work and any
 * other absence that day — same rule as AbsenceForm.
 */
export default {
  name: "EditEntryDialog",
  components: { CommentSuggestInput },
  props: {
    entry: { type: Object, required: true },
    userId: { type: String, default: null }, // admin editing another user's entry
    heading: { type: String, default: "" },
    absenceTypes: { type: Array, default: () => [] },
    absenceLabel: { type: Function, required: true },
    capAbsence: { type: Boolean, default: false },
    referenceEnabled: { type: Boolean, default: false },
  },
  emits: ["saved", "cancel"],
  data() {
    const e = this.entry;
    return {
      loading: !e.isAbsence || this.capAbsence,
      saving: false,
      ceiling: null,
      otherNormal: 0,
      // The absence's day (capAbsence): scheduled dagvinna, dagvinna worked, other absences
      absenceDay: { scheduled: 0, work: 0, otherAbsence: 0 },
      minAbsenceHours: 0.25,
      hold: createHoldStepper(),
      showErrors: false, // set by a click on Vista while something is missing
      form: {
        normalHours: Number(e.normalHours) || 0,
        overtimeHours: Number(e.overtimeHours) || 0,
        comment: e.comment || "",
        reference: e.reference || "",
        typeId: e.timesheetEntryTypeId,
        absenceHours: e.absenceHours != null ? Number(e.absenceHours) : 8,
      },
    };
  },
  computed: {
    /** Dagvinna this line can hold: the day's ceiling minus the user's other lines that day (never below what it already has). */
    maxNormal() {
      if (this.ceiling == null) return 24;
      return Math.max(Number(this.entry.normalHours) || 0, this.ceiling - this.otherNormal);
    },
    /** Most hours the absence can hold (never below what it already has). */
    maxAbsence() {
      if (!this.capAbsence) return 23.75;
      const d = this.absenceDay;
      const room = Math.floor((d.scheduled - d.otherAbsence - d.work) * 4) / 4;
      return Math.max(Number(this.entry.absenceHours) || 0, room);
    },
    blockReason() {
      const f = this.form;
      if (this.entry.isAbsence) {
        if (!(f.absenceHours > 0 && f.absenceHours < 24)) return this.$t("timeRegistration.absenceHoursRange");
        if (f.absenceHours > this.maxAbsence + 1e-9) return this.$t("endOfDay.absenceMax", { hours: this.fmt(this.maxAbsence) });
        return "";
      }
      if (f.normalHours < 0 || f.overtimeHours < 0 || f.normalHours + f.overtimeHours <= 0) return this.$t("endOfDay.registerHours");
      if (!this.isQuarter(f.normalHours) || !this.isQuarter(f.overtimeHours)) return this.$t("timeRegistration.quarterHourSteps");
      if (f.normalHours > this.maxNormal + 1e-9) return this.$t("timeRegistration.maxNormalHoursForDay", { hours: this.fmt(this.maxNormal) });
      if (!f.comment.trim()) return this.$t("endOfDay.descriptionRequired");
      return "";
    },
    /** Which fields the block reason points at. */
    errors() {
      const f = this.form;
      if (this.entry.isAbsence) return { absenceHours: !!this.blockReason };
      const hoursOk = f.normalHours >= 0 && f.overtimeHours >= 0 && f.normalHours + f.overtimeHours > 0
        && this.isQuarter(f.normalHours) && this.isQuarter(f.overtimeHours) && f.normalHours <= this.maxNormal + 1e-9;
      return { hours: !hoursOk, comment: !f.comment.trim() };
    },
  },
  created() { if (!this.entry.isAbsence || this.capAbsence) this.load(); },
  beforeUnmount() { this.hold.stop(); },
  methods: {
    isQuarter(h) { return Number.isFinite(h) && Math.abs(h * 4 - Math.round(h * 4)) < 1e-9; },
    fmt(h) {
      const v = Number(h) || 0;
      return v.toLocaleString(this.$i18n.locale?.value || this.$i18n.locale || "is-IS", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    /** Highest value the field can take, on the quarter-hour grid the server requires. */
    maxHours(field) {
      if (field === "absenceHours") return this.maxAbsence;
      const max = field === "normalHours" ? this.maxNormal : 24 - (Number(this.form.normalHours) || 0);
      return Math.floor(max * 4) / 4;
    },
    /** Returns whether the value moved. */
    step(field, direction, held) {
      const value = Number(this.form[field]) || 0;
      const next = nextHours(value, direction, this.maxHours(field), {
        held,
        min: field === "absenceHours" ? this.minAbsenceHours : 0,
        quarters: field === "overtimeHours",
      });
      this.form[field] = next;
      return next !== value;
    },
    stepButton(field, direction) {
      return this.hold.button(held => this.step(field, direction, held));
    },
    async load() {
      const date = String(this.entry.entryDate).substring(0, 10);
      const userParam = this.userId ? `&userId=${this.userId}` : "";
      try {
        const [planRes, entriesRes] = await Promise.all([
          api.get(`/Timesheet/day-plan?dateFrom=${date}&dateTo=${date}${userParam}`),
          api.get(`/Timesheet?dateFrom=${date}&dateTo=${date}${userParam}`),
        ]);
        const plan = (planRes.data || [])[0];
        const entries = (entriesRes.data?.days || []).flatMap(d => d.entries || []);
        if (this.entry.isAbsence) {
          const scheduled = plan ? Number(plan.scheduledNormalHours) || 0 : 0;
          this.absenceDay = {
            scheduled,
            // Dagvinna already worked: hours-only lines carry normalHours, clocked entries count their duration
            work: entries.filter(e => !e.isAbsence)
              .reduce((s, e) => s + (e.normalHours != null ? Number(e.normalHours) : (e.adjustedDurationMinutes || 0) / 60), 0),
            otherAbsence: entries.filter(e => e.isAbsence && e.id !== this.entry.id)
              .reduce((s, e) => s + (e.absenceHours != null ? Number(e.absenceHours) : scheduled), 0),
          };
          return;
        }
        this.ceiling = plan ? Number(plan.normalHoursCeiling) || 0 : null;
        // The user's other end-of-day lines that day share the dagvinna ceiling
        this.otherNormal = entries
          .filter(e => e.id !== this.entry.id && !e.isAbsence && !e.timeRegistrationGroupId && !e.realClockIn && (e.projectId?.value ?? e.projectId))
          .reduce((s, e) => s + (Number(e.normalHours) || 0), 0);
      } catch (e) {
        console.error(e); // eslint-disable-line no-console
        this.$toast?.danger(this.$t("common.unableToRetrieveData"), { timeout: 0 });
      } finally {
        this.loading = false;
      }
    },
    onSaveClick() {
      if (this.blockReason) { this.showErrors = true; return; }
      this.save();
    },
    async save() {
      const f = this.form;
      let body;
      if (this.entry.isAbsence) {
        body = { absenceHours: f.absenceHours, timesheetEntryTypeId: f.typeId };
      } else {
        body = { normalHours: f.normalHours, overtimeHours: f.overtimeHours, comment: f.comment.trim() };
        if (this.referenceEnabled) body.reference = f.reference.trim();
      }
      this.saving = true;
      try {
        await api.put(`/Timesheet/${this.entry.id}`, body);
        clearCommentSuggestions();
        this.$emit("saved");
      } catch (e) {
        const msg = e?.response?.data?.detail || e?.response?.data?.errors?.join?.(" ") || e?.response?.data || this.$t("common.unableToUpdate");
        this.$toast?.danger(typeof msg === "string" ? msg : JSON.stringify(msg), { timeout: 0 });
        console.error(e); // eslint-disable-line no-console
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.reference-input { max-width: 9rem; }
.section-label { font-size: 0.75rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: #67748e; }
.overtime-label { color: #8a5a00; }
.project-current { padding: 0.6rem 0.9rem; border-radius: 0.5rem; background: #f6f7fb; }
.stepper { display: flex; align-items: center; gap: 0.35rem; }
.stepper .btn { flex: 1 1 0; padding: 0.5rem 0; font-size: 1.75rem; font-weight: 600; line-height: 1.3; text-transform: none; touch-action: manipulation; user-select: none; -webkit-user-select: none; -webkit-touch-callout: none; }
.stepper-value { flex: 1.4 1 0; display: flex; flex-direction: column; align-items: center; line-height: 1; }
.stepper-value input { font-size: 1.25rem; font-weight: 700; color: #344767; padding: 0.35rem 0.25rem; border: 1px solid #d2d6da !important; border-radius: 0.5rem; }
.stepper-value span { font-size: 1.75rem; font-weight: 700; color: #344767; }
.stepper-value small { font-size: 0.75rem; color: #67748e; }
.stepper-value input.is-invalid { border-color: #ea0606 !important; color: #ea0606; }
.field-error { font-weight: 600; color: #ea0606; }
.save-blocked { opacity: 0.55; }
</style>
